import React from 'react';
import { Layout, Menu, Button, Dropdown, Avatar, Badge, Grid, Drawer, Input, Tooltip } from 'antd';
import { HashRouter as Router, Routes, Route, Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  DashboardOutlined,
  ApiOutlined,
  AlertOutlined,
  AreaChartOutlined,
  UserOutlined,
  ToolOutlined,
  SettingOutlined,
  SafetyCertificateOutlined,
  LogoutOutlined,
  BellOutlined,
  MenuOutlined,
  CloseOutlined
} from '@ant-design/icons';
import './App.css';

// Importar páginas
import {
  Dashboard,
  Devices,
  Alerts,
  Analytics,
  Users,
  Compliance,
  Operations,
  Settings,
  Login
} from './pages';
import brandLogoPng from './assets/logo-nc3.png';

const { Header, Content, Sider } = Layout;
const { useBreakpoint } = Grid;

function ShellLayout({ user, setUser, unreadAlerts }) {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = React.useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [navQuery, setNavQuery] = React.useState('');
  const [logoVisible, setLogoVisible] = React.useState(true);
  const searchRef = React.useRef(null);
  const brandLogoSrc = brandLogoPng;

  const normalizePath = React.useCallback((rawPath) => {
    const value = String(rawPath || '/').trim();
    if (!value || value === '/') return '/';
    const noTrailing = value.replace(/\/+$/, '');
    return noTrailing || '/';
  }, []);

  // Landing en Dashboard solo cuando la ruta es vacia
  React.useEffect(() => {
    if (location.pathname === '' ) {
      navigate('/', { replace: true });
    }
    localStorage.removeItem('app_last_route');
    localStorage.removeItem('dashboard_main_tab');
    localStorage.removeItem('dashboard_active_main_tab');
    sessionStorage.removeItem('app_last_path_before_reload');
  }, [location.pathname, navigate]);

  React.useEffect(() => {
    const onBeforeUnload = () => {
      const fullPath = `${location.pathname}${location.search || ''}${location.hash || ''}`;
      sessionStorage.setItem('app_last_path_before_reload', fullPath);
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [location.hash, location.pathname, location.search]);

  const userMenuItems = [
    { key: 'profile', icon: <UserOutlined />, label: 'Perfil' },
    { key: 'settings', label: 'Configuración' },
    { type: 'divider' },
    { key: 'logout', icon: <LogoutOutlined />, label: 'Cerrar Sesión', danger: true }
  ];

  const menuItems = [
    {
      key: '/',
      icon: <DashboardOutlined />,
      label: <Link to="/">Dashboard</Link>,
    },
    {
      key: '/devices',
      icon: <ApiOutlined />,
      label: <Link to="/devices">Dispositivos</Link>,
    },
    {
      key: '/alerts',
      icon: <AlertOutlined />,
      label: <Link to="/alerts">Alertas e Incidentes</Link>,
      badge: unreadAlerts > 0 ? unreadAlerts : null,
    },
    {
      key: '/analytics',
      icon: <AreaChartOutlined />,
      label: <Link to="/analytics">Analítica</Link>,
    },
    {
      key: '/compliance',
      icon: <SafetyCertificateOutlined />,
      label: <Link to="/compliance">Cumplimiento</Link>,
    },
    {
      key: '/operations',
      icon: <ToolOutlined />,
      label: <Link to="/operations">Operación</Link>,
    },
    {
      key: '/settings',
      icon: <SettingOutlined />,
      label: <Link to="/settings">Configuración</Link>,
    },
  ];

  if (user?.role === 'admin') {
    menuItems.push({ key: '/users', icon: <UserOutlined />, label: <Link to="/users">Usuarios</Link> });
  }

  const filteredMenuItems = React.useMemo(() => {
    const q = navQuery.trim().toLowerCase();
    if (!q) return menuItems;
    return menuItems.filter((item) => {
      const text = String(item.key || '').toLowerCase();
      const linkText = String(item.label?.props?.children || '').toLowerCase();
      return text.includes(q) || linkText.includes(q);
    });
  }, [menuItems, navQuery]);

  React.useEffect(() => {
    const onHotkey = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onHotkey);
    return () => window.removeEventListener('keydown', onHotkey);
  }, []);

  const currentPath = normalizePath(location.pathname);
  const selectedKey = menuItems.some((m) => m.key === currentPath) ? currentPath : '/';

  const onSearchNavigate = (value) => {
    const query = String(value || '').trim().toLowerCase();
    if (!query) return;
    const target = menuItems.find((item) => {
      const labelText = String(item.label?.props?.children || '').toLowerCase();
      return String(item.key).toLowerCase().includes(query) || labelText.includes(query);
    });
    if (target) {
      navigate(target.key);
      setMobileMenuOpen(false);
      setNavQuery('');
    }
  };

  React.useEffect(() => {
    const normalized = normalizePath(location.pathname);
    if (location.pathname !== normalized) {
      navigate(`${normalized}${location.search || ''}${location.hash || ''}`, { replace: true });
    }
  }, [location.hash, location.pathname, location.search, navigate, normalizePath]);

  // Eliminar restauración automática a /devices

  const renderMenuLabel = (item) => {
    if (item.key === '/alerts' && unreadAlerts > 0) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <Link to={item.key}>Alertas e Incidentes</Link>
          <Badge count={unreadAlerts} size="small" />
        </div>
      );
    }
    return item.label;
  };

  const uiMenuItems = filteredMenuItems.map((item) => ({
    ...item,
    label: renderMenuLabel(item)
  }));

  return (
    <Layout className="app-shell" style={{ minHeight: '100vh' }}>
      {!isMobile && (
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          width={260}
          className="app-sider"
        >
          <div className="logo">
            <div className={`app-brand ${collapsed ? 'collapsed' : ''}`}>
              {logoVisible ? (
                <img
                  src={brandLogoSrc}
                  alt="NC3"
                  className="app-brand-logo"
                  onError={() => setLogoVisible(false)}
                />
              ) : (
                <div className="app-brand-logo app-brand-fallback">NC3</div>
              )}
              {!collapsed && <h1 className="app-brand-title">NC3 Control Hub</h1>}
            </div>
          </div>
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={[selectedKey]}
            items={uiMenuItems}
            className="app-menu"
          />
        </Sider>
      )}

      <Layout className="site-layout">
        <Header className="app-header" style={{ padding: isMobile ? '0 12px' : '0 20px' }}>
          <div className="app-header-inner">
            <Button
              type="text"
              icon={isMobile ? <MenuOutlined /> : (collapsed ? <MenuOutlined /> : <CloseOutlined />)}
              onClick={() => isMobile ? setMobileMenuOpen(true) : setCollapsed(!collapsed)}
              style={{ fontSize: 18, width: 44, height: 44, color: '#f8fafc' }}
            />

            {isMobile && (
              <div className="app-mobile-brand">
                {logoVisible ? (
                  <img
                    src={brandLogoSrc}
                    alt="NC3"
                    className="app-mobile-brand-logo"
                    onError={() => setLogoVisible(false)}
                  />
                ) : (
                  <div className="app-mobile-brand-logo app-brand-fallback">NC3</div>
                )}
                <span>NC3 Control Hub</span>
              </div>
            )}

            {!isMobile && (
              <Input.Search
                ref={searchRef}
                className="quick-nav-search"
                placeholder="Ir a... (Ctrl+K)"
                value={navQuery}
                onChange={(e) => setNavQuery(e.target.value)}
                onSearch={onSearchNavigate}
                allowClear
              />
            )}

            <div style={{ display: 'flex', gap: isMobile ? 8 : 16, alignItems: 'center' }}>
              <Tooltip title="Ir a alertas">
                <Badge count={unreadAlerts} size="small" style={{ backgroundColor: '#f43f5e' }}>
                  <Button
                    type="text"
                    icon={<BellOutlined style={{ fontSize: 18, color: '#f8fafc' }} />}
                    onClick={() => navigate('/alerts')}
                  />
                </Badge>
              </Tooltip>

              <Dropdown
                menu={{
                  items: userMenuItems,
                  onClick: ({ key }) => {
                    if (key === 'settings') {
                      navigate('/settings');
                      return;
                    }
                    if (key === 'profile') {
                      navigate('/users');
                      return;
                    }
                    if (key === 'logout') {
                      localStorage.clear();
                      setUser(null);
                    }
                  }
                }}
                placement="bottomRight"
                trigger={['click']}
              >
                <Button type="text" style={{ display: 'flex', alignItems: 'center', gap: 8, paddingInline: isMobile ? 4 : 8, color: '#f8fafc' }}>
                  <Avatar size={isMobile ? 'small' : 'default'} icon={<UserOutlined />} />
                  {!isMobile && <span>{user?.username || 'Usuario'}</span>}
                </Button>
              </Dropdown>
            </div>
          </div>
        </Header>

        <Content className="app-content" style={{ margin: isMobile ? 8 : 20, padding: isMobile ? 10 : 20 }}>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/devices" element={<Devices />} />
            <Route path="/alerts" element={<Alerts />} />
            <Route path="/analytics" element={<Analytics />} />
            <Route path="/compliance" element={<Compliance user={user} />} />
            <Route path="/operations" element={<Operations user={user} />} />
            <Route path="/settings" element={<Settings user={user} />} />
            {user?.role === 'admin' && <Route path="/users" element={<Users />} />}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Content>
      </Layout>

      {isMobile && (
        <Drawer
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          placement="left"
          width="min(86vw, 320px)"
          bodyStyle={{ padding: 0, background: '#0f172a' }}
          headerStyle={{ background: '#0f172a', color: '#fff', borderBottom: '1px solid rgba(255,255,255,0.08)' }}
          title={(
            <div className="app-mobile-brand">
              <img src={brandLogoSrc} alt="NC3" className="app-mobile-brand-logo" />
              <span style={{ color: '#fff' }}>NC3 Control Hub</span>
            </div>
          )}
        >
          <div style={{ padding: 12 }}>
            <Input.Search
              ref={searchRef}
              placeholder="Ir a..."
              value={navQuery}
              onChange={(e) => setNavQuery(e.target.value)}
              onSearch={onSearchNavigate}
              allowClear
            />
          </div>
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={[selectedKey]}
            items={uiMenuItems}
            onClick={() => setMobileMenuOpen(false)}
            style={{ borderInlineEnd: 'none' }}
          />
        </Drawer>
      )}
    </Layout>
  );
}

function App() {
  const [user, setUser] = React.useState(null);
  const [authChecked, setAuthChecked] = React.useState(false);
  const [unreadAlerts, setUnreadAlerts] = React.useState(0);

  React.useEffect(() => {
    const loadSession = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setAuthChecked(true);
        return;
      }

      try {
        const res = await axios.get('/api/auth/me', {
          headers: { Authorization: `Bearer ${token}` }
        });
        const currentUser = res.data?.user || null;
        if (currentUser) {
          setUser(currentUser);
          localStorage.setItem('user', JSON.stringify(currentUser));
        } else {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      } catch {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      } finally {
        setAuthChecked(true);
      }
    };

    loadSession();
  }, []);

  React.useEffect(() => {
    if (!user) return undefined;

    const token = localStorage.getItem('token');
    if (!token) return undefined;

    const loadUnread = async () => {
      try {
        const res = await axios.get('/api/alerts/incidents', {
          headers: { Authorization: `Bearer ${token}` }
        });
        const incidents = res.data?.incidents || [];
        const unread = incidents.filter((i) => i.is_active && !i.acknowledged).length;
        setUnreadAlerts(unread);
      } catch {
        // Mantener la UI estable aun cuando falle telemetria de alertas.
      }
    };

    loadUnread();
    const t = setInterval(loadUnread, 10000);
    return () => clearInterval(t);
  }, [user]);

  if (!authChecked) {
    return null;
  }

  if (!user) {
    return <Login setUser={setUser} />;
  }

  return (
    <Router>
      <ShellLayout user={user} setUser={setUser} unreadAlerts={unreadAlerts} />
    </Router>
  );
}

export default App;
